import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, DatePicker, Modal, Popover, Select, Spin, Table, Tooltip, message} from 'antd'
import {CaretDownOutlined, CaretRightOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import moment from 'moment'
import queryString from 'query-string'
import {
    exportOutputReport,
    getOutputDropped,
    getOutputReport,
    getOutputReportByStage,
    getStages,
} from '../../../../services/api/apiProductionPlanning'
import {DROP_REASON_LABELS, OWNERS, ROUTES} from '../../constants'
import {OUTPUT_LABELS, OUTPUT_TOOLTIPS, outputReadingNotesOf} from '../../constants/glossary'
import {anyExpandedOf, groupsOf, rowsOf} from '../../helpers/reportRows'
import {
    DASH,
    formatHours,
    formatInt,
    formatPercent,
    formatVnDateTime,
    percentOf,
} from '../../helpers/format'
import {outputCaption} from '../../helpers/outputCaption'
import {
    deltaOf,
    lowCoverageStagesOf,
    freshnessOf,
    milestoneStagesOf,
    noEfficiencyReasonOf,
    previousRangeLabelOf,
    pureEfficiencyNoteOf,
    rateSanityWarningsOf,
    shouldShowMatrix,
} from '../../helpers/efficiencyReport'
import {actorEfficiencyColumns, stageEfficiencyColumns} from './efficiencyColumns'
import {kpiCards} from './kpiCards'
import {rangeEndOf, rangeStartOf, reportPresetRanges, vnDayRangeIso} from '../../helpers/dateRange'
import useUrlFilters from '../../../helpers/useUrlFilters'
import {setUrlParams} from '../../../../share/urlParams'
import {errorCode, errorMessage} from '../../helpers/apiError'
import {downloadBlob, exportStamp} from '../../helpers/download'
import Notice from '../shared/Notice'
import TeamEfficiencyPanel from './TeamEfficiencyPanel'
import EfficiencyMatrix from './EfficiencyMatrix'
import StageEfficiencyChart from './StageEfficiencyChart'
import ReportNotices from './ReportNotices'
import ReportSubtitle from './ReportSubtitle'
import AccountLabel from './AccountLabel'
import LoadError from '../shared/LoadError'
import PercentTag from '../shared/PercentTag'
import {CountUnitTag, TimeBasisTag} from '../shared/StageBadges'
import OutputDetailPage from './OutputDetailPage'
import SuspectPage from './SuspectPage'
import GlossaryPage from './GlossaryPage'

const {Option} = Select

/**
 * SCR-M18-001 — Sản lượng & giờ định mức theo nhân viên.
 *
 * Quy ước xuyên suốt màn hình (UI-01): **chỉ số đếm mới được bấm**. Ô là giờ, là phần trăm, hoặc có
 * giá trị 0 thì không bấm được và không hiển thị màu xanh. Bấm vào con số **không** làm mở rộng dòng
 * tài khoản — đây là hai hành vi tách biệt (B-AC-11).
 */
/**
 * SCR-M18-001 (gộp theo TÀI KHOẢN) và SCR-M18-005 (gộp theo CÔNG ĐOẠN) là MỘT component.
 *
 * Hai màn nhìn cùng một tập dữ liệu, chỉ đảo trục nhóm — tách thành hai file là chép 1.000 dòng bộ
 * lọc, dải KPI, 12 cột và toàn bộ đường khoan sâu, rồi sửa một bên quên bên kia. Điều khiến việc gộp
 * này RẺ là phần tử CON của hai response là cùng một object (tài khoản × công đoạn, mang đủ cả
 * `actor` lẫn `stageCode` và mọi con số), nên phần lớn màn dùng chung.
 *
 * ⚠️ Nhưng đây KHÔNG phải một prop trang trí. Đây là danh sách DUY NHẤT các điểm rẽ theo `byStage`
 *    (`OutputByStagePage` trỏ về đây, không giữ bản sao — hai bản kê rồi sẽ lệch nhau):
 *
 *      1. endpoint gọi                                `getOutputReportByStage` vs `getOutputReport`
 *      2. deps của `useCallback fetchReport`
 *      3. hàm gom nhóm `groups`
 *      4. `actorOptions` — nguồn dữ liệu ô lọc Tài khoản
 *      5. render cột "Tài khoản"   — CẢ dòng cha lẫn dòng con
 *      6. render cột "Công đoạn"   — CẢ dòng cha lẫn dòng con
 *      7. `orderedColumns` — thứ tự hai cột đầu kèm vị trí `fixed`
 *      8. Popover "Số lần làm lại" — nhãn và trục gom
 *      9. tiêu đề trang
 *
 *    Mọi thay đổi ở file này phải thử CẢ HAI đường. `grep -n byStage` liệt kê từng dòng rẽ (đừng
 *    dùng `grep -c`: nó đếm cả mấy dòng chú thích này).
 */
/** Chờ bấy nhiêu ms mới hiện spinner — dưới ngưỡng này thì lượt tải xong trước khi mắt kịp thấy. */
const SPIN_DELAY_MS = 200

/**
 * Hai cột mô tả thuộc tính CÔNG ĐOẠN, ẩn khi chưa mở dòng nào.
 *
 * ⚠️ Danh sách này phải khớp `key` của hai cột đó. Đổi `key` mà quên đây thì cột không ẩn nữa và
 *    không có gì báo — bảng chỉ rộng thêm 240px.
 */
const META_COLUMN_KEYS = ['countUnit', 'timeBasis']

/** Tổng `width` của hai cột trên — trừ khỏi `scroll.x` khi chúng ẩn. */
const META_COLUMNS_WIDTH = 240

function OutputReportPage({history, location, groupBy = 'actor'}) {
    const byStage = groupBy === 'stage'

    // `actors`/`stageCodes` vẫn qua `useUrlFilters` bình thường — mảng RỖNG là mặc định ổn định và
    // đúng nghĩa "không lọc gì", ẩn khỏi URL lúc đó là hợp lý.
    const filterDefaults = useMemo(() => ({actors: [], stageCodes: []}), [])
    /*
     * Dựng MỘT lần. `reportPresetRanges` trả HÀM cho từng khoảng nên mốc vẫn tính lúc bấm (mở màn
     * lúc 23:58, bấm lúc 00:02 vẫn ra ngày mới); bảng nhãn thì không có lý do dựng lại mỗi render.
     */
    /*
     * ⚠️ KHÔNG `useMemo`: `recentMonthRanges` dựng NHÃN từ `moment()` ngay lúc gọi, còn khoảng thì
     *    tính lúc bấm. Đóng băng lúc mount là tab mở từ 31/10 qua đêm sẽ vẫn hiện "Tháng 10" trong
     *    khi bấm vào trả về khoảng của 01/11. Dựng lại vài object mỗi lượt vẽ rẻ hơn hẳn một nhãn
     *    nói sai. (Vòng review 2.)
     */
    const datePresets = reportPresetRanges()
    const [filters, setFilter] = useUrlFilters(filterDefaults, {history, location})
    const {actors, stageCodes} = filters

    /**
     * `from`/`to` CỐ Ý tách khỏi `useUrlFilters` ở trên, không đi chung với `actors`/`stageCodes`.
     *
     * Hook đó CHỈ ghi lên URL khoá KHÁC mặc định (đúng thiết kế, xem `helpers/useUrlFilters.js`) —
     * nhưng nếu đặt mặc định `from = moment().startOf('day')` thì đó là giá trị ỔN ĐỊNH, tính lại
     * trong cùng một ngày luôn ra y hệt. Hook vì vậy KHÔNG phân biệt được "chưa ai đổi" với "cố tình
     * chọn đúng đầu ngày hôm nay" — cả hai bị coi là "= mặc định" và bị xoá khỏi URL, trong khi `to`
     * (`moment()`, đúng khoảnh khắc) hầu như không bao giờ trùng lại nên luôn được giữ. Kết quả quan
     * sát được: `to` có trên URL, `from` thì mất — dù ô nhập vẫn hiển thị giá trị bình thường.
     *
     * Với trang mà mục đích chính là SHARE LINK có khoảng thời gian, thiếu `from` là lỗi thật: mở lại
     * link vào NGÀY KHÁC thì `from` bị tính lại thành "đầu ngày hôm đó", sai hẳn khoảng đã chia sẻ.
     * Quản riêng bằng `useState` (khởi tạo từ URL nếu có, không thì "hôm nay") + effect LUÔN ghi
     * `from`/`to` lên URL không qua so sánh mặc định, để tách hẳn khỏi vòng lặp "hook tự xoá lại
     * những gì effect vừa ghi" nếu vẫn cố nhét chung vào `useUrlFilters`.
     */
    /*
     * ⚠️ Mốc trên URL đọc theo NGÀY VN, không theo giờ máy.
     *
     *    URL nay ghi ngày lịch (`2026-10-28`), nhưng link CŨ còn mang mốc ISO đầy đủ. Mở một link
     *    cũ trên máy không ở UTC+7 thì `moment(raw).format('YYYY-MM-DD')` cho một ngày KHÁC ngày
     *    người gửi đang xem — và effect đồng bộ URL ghi đè ngay ngày sai đó, nên chia sẻ tiếp là
     *    lan tiếp. `utcOffset(7)` cho mọi người đọc cùng một ngày lịch.
     */
    const dayFromUrl = (raw, fallback) => (raw ? moment(raw).utcOffset(7) : fallback())

    const [from, setFrom] = useState(() =>
        dayFromUrl(queryString.parse(location.search).from, () => moment().startOf('day')))
    const [to, setTo] = useState(() => {
        const raw = queryString.parse(location.search).to

        // Mặc định là HẾT ngày hôm nay (23:59:59.999), không phải "bây giờ". Lấy "bây giờ" thì khoảng
        // mặc định co lại theo từng lần mở màn hình, nên hai người mở cách nhau một tiếng thấy hai
        // con số khác nhau cho cùng một ngày mà không ai đổi bộ lọc nào.
        return dayFromUrl(raw, () => moment().endOf('day'))
    })

    useEffect(() => {
        const currentQuery = queryString.parse(location.search)
        /*
         * ⚠️ Ghi NGÀY (`YYYY-MM-DD`), không ghi mốc ISO.
         *
         *    `toISOString()` là một mốc UTC, nên link tạo trên máy UTC+7 mở trên máy UTC+0 cho ra
         *    một ngày KHÁC — người nhận đọc một báo cáo khác cái người gửi đang xem, mà cả hai đều
         *    thấy một bảng đầy số nên không ai nghi ngờ. Ngày lịch thì đọc ở đâu cũng là ngày đó.
         *
         *    Service nhận date-only và tự snap về biên ngày VN (`vnRangeOf`), nên đây cũng là dạng
         *    nó hiểu đúng nhất. Link CŨ mang mốc ISO vẫn mở được: `moment(raw)` nhận cả hai dạng.
         */
        const fromIso = from.format('YYYY-MM-DD')
        const toIso = to.format('YYYY-MM-DD')

        if (currentQuery.from === fromIso && currentQuery.to === toIso) return

        /*
         * `setUrlParams` chứ KHÔNG phải `queryString.stringify({...currentQuery, …})`.
         *
         * Spread bê nguyên MỌI khoá đang có trên URL, kể cả `token`/`refresh` mà
         * `WithAuthentication` để sót lại trong `location` của router (nó dọn thanh địa chỉ bằng
         * `window.history.replaceState`, lệnh mà react-router không biết). Đo ở vòng review 14:
         * lời `replace` này THẬT SỰ mang JWT — URL cuối sạch chỉ vì `useUrlFilters` của bộ lọc chi
         * tiết tình cờ chạy SAU và ghi đè. Đổi chỗ hai hook là token trụ lại ngay.
         *
         * `setUrlParams` giữ nguyên mọi khoá lạ khác và luôn lọc `AUTH_PARAMS`.
         */
        history.replace({
            pathname: location.pathname,
            search: setUrlParams(location.search, {from: fromIso, to: toIso}),
        })
    }, [from, to, history, location.pathname, location.search])

    // Modal Chi tiết sản lượng (SCR-M18-002) đồng bộ lên CHÍNH URL của báo cáo (bộ lọc riêng, khoá
    // tên khác `actors`/`stageCodes` số nhiều của báo cáo) — copy link lúc modal đang mở, người nhận
    // mở ra thấy modal tự mở lại đúng dòng đó. Trạng thái mở nằm ở cờ riêng `detailOpen` — xem
    // `detailDefaults` ngay dưới.
    //
    // `detailFrom`/`detailTo` RỖNG mặc định = lấy khoảng thời gian của CHÍNH báo cáo (`from`/`to`
    // phía trên) — đúng yêu cầu "mặc định lấy ở page cha". Bấm Áp dụng trong modal với khoảng khác
    // thì hai khoá này được ghi giá trị thật, tách khỏi bộ lọc báo cáo, và modal dùng khoảng RIÊNG đó
    // từ giờ trở đi kể cả khi báo cáo phía sau đổi khoảng của chính nó.
    const detailDefaults = useMemo(
        () => ({
            // `detailOpen` là cờ RIÊNG, cùng khuôn với `suspectOpen` bên dưới — KHÔNG suy trạng thái
            // mở từ dữ liệu lọc. Suy từ `detailActor` là lỗi đã gặp: dòng cha màn gộp theo công đoạn
            // khoan sâu không kèm tài khoản nên modal câm.
            //
            // Hôm nay mọi đường khoan sâu đều đặt ít nhất một trục, nên cờ này CHƯA thay đổi hành vi
            // so với `detailActor || detailStageCode`. Giữ nó vì trạng thái "đang mở" là việc của
            // riêng nó: thêm một đường khoan sâu không kèm trục nào (toàn báo cáo) thì chỉ phải đặt
            // cờ, không phải đi sửa lại điều kiện mở.
            detailOpen: '',
            detailActor: '', detailStageCode: '', detailOnly: '', detailFrom: '', detailTo: '',
            // `suspectOpen` là cờ RIÊNG, không suy từ `suspectActor`: danh sách này mở được ở phạm vi
            // toàn báo cáo (không gắn tài khoản nào), nên suy từ actor sẽ không mở được ca đó.
            suspectOpen: '', suspectActor: '', suspectStageCode: '',
            glossaryOpen: '',
        }),
        []
    )
    const [detailFilters, setDetailFilter] = useUrlFilters(detailDefaults, {history, location})
    /*
     * Bộ lọc ĐÃ ÁP DỤNG — tức bộ lọc sinh ra đúng những con số đang hiển thị.
     *
     * `from`/`to`/`actors`/`stageCodes` là state SỐNG của các ô lọc, đổi ngay khi người dùng chọn;
     * báo cáo thì chỉ nạp lại khi bấm "Áp dụng"/"Đặt lại". Mọi thứ MÔ TẢ hoặc PHÁI SINH từ bảng
     * phải đọc `applied`, nếu không chúng nói về một khoảng khác với chính bảng bên dưới:
     *   · Xuất Excel — file mang đi đối soát không khớp màn hình;
     *   · danh sách "N dòng bị loại" và phụ đề khoảng thời gian;
     *   · hai modal khoan sâu — chúng mở bằng cú bấm thẳng vào một con số TRONG BẢNG, mà con số đó
     *     thuộc khoảng đã áp dụng, nên mở theo khoảng đang chọn là mâu thuẫn ngay trên màn hình.
     */
    const [applied, setApplied] = useState(null)
    const appliedRange = applied || {from, to}
    /*
     * ⚠️ MỘT nguồn duy nhất cho khoảng gửi lên service, và mọi đường phải dùng nó.
     *
     *    Lượt trước chỉ đổi `fetchReport` sang `vnDayRangeIso` rồi bỏ quên bốn đường còn lại
     *    (xuất Excel, modal chi tiết, modal nghi ngờ, modal dòng bị loại). Xuất Excel gọi ĐÚNG
     *    cùng method service với báo cáo, nên trên máy không ở UTC+7 thì màn hình đầy số còn file
     *    tải về có sáu cột hiệu suất rỗng — lỗi không biến mất, nó chuyển sang file và nay LỆCH
     *    với màn hình. Hai modal khoan sâu thì mở ra tập item lệch 7 giờ so với con số vừa bấm.
     */
    const appliedVnRange = vnDayRangeIso(appliedRange.from, appliedRange.to)
    // `|| detailActor || detailStageCode`: đỡ các link khoan sâu đã chia sẻ TRƯỚC khi có `detailOpen`.
    // Màn này đã chạy trên master (merge `9196d96f`) với cổng là `detailActor`, và chính chú thích
    // phía trên mời người dùng copy link lúc modal đang mở. Thiếu nhánh này thì link cũ mở ra không
    // có modal — im lặng, không lỗi, mà `detailActor` vẫn trụ trên URL nên link trông vẫn hợp lệ.
    // Đây CHỈ là đường di trú lúc đọc URL: mọi đường từ UI đều đi qua `goDetail`, vốn luôn ghi
    // `detailOpen: '1'`, nên ca "mọi tài khoản, mọi công đoạn" trong tương lai vẫn mở được.
    const detailOpen = detailFilters.detailOpen
        || detailFilters.detailActor
        || detailFilters.detailStageCode
    const detail = detailOpen
        ? {
            from: detailFilters.detailFrom || appliedVnRange.from,
            to: detailFilters.detailTo || appliedVnRange.to,
            // `undefined` = không lọc theo tài khoản; chuỗi rỗng sẽ bị `clean()` bỏ nhưng để
            // `undefined` cho thẳng ý, và để `actorLabel` bên màn chi tiết rơi đúng nhánh
            // "Tất cả tài khoản".
            actor: detailFilters.detailActor || undefined,
            stageCode: detailFilters.detailStageCode || undefined,
            only: detailFilters.detailOnly || undefined,
        }
        : null
    const closeDetail = () => setDetailFilter({
        detailOpen: '', detailActor: '', detailStageCode: '', detailOnly: '', detailFrom: '', detailTo: '',
    })
    const suspect = detailFilters.suspectOpen
        ? {
            from: appliedVnRange.from,
            to: appliedVnRange.to,
            actor: detailFilters.suspectActor || undefined,
            stageCode: detailFilters.suspectStageCode || undefined,
        }
        : null
    const closeSuspect = () => setDetailFilter({suspectOpen: '', suspectActor: '', suspectStageCode: ''})
    const glossaryOpen = !!detailFilters.glossaryOpen
    const closeGlossary = () => setDetailFilter({glossaryOpen: ''})
    // Bấm "Áp dụng" NGAY TRONG modal (khoan sâu tài khoản/công đoạn/khoảng thời gian khác) — reset
    // `detailOnly` vì thẻ lọc phụ đó gắn với ngữ cảnh khoan sâu CŨ, giữ lại dễ gây hiểu nhầm số liệu.
    const applyDetailFilters = (next) => setDetailFilter({
        detailActor: next.actor || '',
        detailStageCode: next.stageCode || '',
        detailOnly: '',
        detailFrom: next.from || '',
        detailTo: next.to || '',
    })

    const [stageOptions, setStageOptions] = useState([])
    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expanded, setExpanded] = useState({})
    const [dropped, setDropped] = useState(null)
    const [exporting, setExporting] = useState(false)

    /*
     * Khối "Tổng quan hiệu suất đội ngũ" gập/mở, và việc mở nó là thứ DUY NHẤT bật `comparePrevious`.
     *
     * ⚠️ Cờ đó khiến server chạy thêm một lượt đọc `itemhistories` (22,4 triệu doc) cho kỳ trước.
     *    Bật mặc định là bắt mọi lượt xem trả giá cho một khối phần lớn không ai mở ra.
     */
    const [compareOpen, setCompareOpen] = useState(false)

    /**
     * `override` phủ được CẢ bộ lọc, không chỉ khoảng ngày.
     *
     * `setFilter`/`setFrom` là cập nhật bất đồng bộ, nên handler nào vừa reset state vừa gọi
     * `fetchReport` ngay sau đó vẫn đang giữ closure của render CŨ. Trước đây `override` chỉ
     * phủ `from`/`to`, nên nút "Đặt lại" xoá chip lọc trên UI nhưng vẫn gửi `actors`/`stageCodes`
     * cũ lên service — người dùng đọc số của một tập con mà tưởng là toàn nhà máy.
     */
    /*
     * Số thứ tự lượt gọi. Lượt sau CHỈ được ghi kết quả nếu nó vẫn là lượt mới nhất.
     *
     * ⚠️ Không có nó thì response CŨ đè lên kết quả MỚI, và ở màn này cửa sổ để điều đó xảy ra rất
     *    rộng: một khoảng 92 ngày đi đường thường tốn tới ~90 giây, nên người dùng đổi bộ lọc rồi
     *    bấm lại trong lúc chờ là chuyện thường. Khi lượt cũ về sau, bảng hiện số của bộ lọc CŨ bên
     *    dưới ô lọc MỚI — và `applied` (thứ nút Xuất Excel đọc) cũng bị kéo theo, nên file tải về
     *    khớp với bảng đang sai chứ không khớp với màn hình. Cùng lỗi đã vá cho M17 trong chính MR
     *    này. (Vòng review 2, G-R5.)
     */
    const runIdRef = useRef(0)
    /*
     * Bộ lọc của lượt VỪA GỬI — ghi TRƯỚC `await`, nên lượt HỎNG cũng có.
     *
     * ⚠️ Khác `applied`: `applied` chỉ được ghi ở nhánh THÀNH CÔNG, nên dùng nó cho "Thử lại" là
     *    phát lại bộ lọc của lượt thành công TRƯỚC ĐÓ. Đo thật: mount (tháng 10, OK) → "Đặt lại"
     *    (04/11, HỎNG) → "Thử lại" ⇒ gửi lại tháng 10, bảng hiện số tháng 10 trong khi ô chọn ngày
     *    vẫn 04/11, và `applied` kéo theo nên Xuất Excel cũng ra khoảng cũ. Đúng lớp lỗi "bảng nói
     *    về một bộ lọc không ai bấm" mà `runId` sinh ra để chặn. (Vòng review 4, M3.)
     */
    const lastRequestedRef = useRef(null)

    const fetchReport = useCallback(async (override, compareOverride, keepExpanded) => {
        const range = {from, to, actors, stageCodes, ...override}

        /*
         * ⚠️ Kiểm khoảng ngày TRƯỚC khi tăng `runId`. Tăng trước thì một lượt bấm với khoảng sai sẽ
         *    vô hiệu hoá lượt hợp lệ ĐANG chạy: lượt kia về sau bị vứt, bảng ở nguyên kết quả cũ
         *    hơn, và lời duy nhất người dùng nhận được là câu báo về khoảng ngày.
         *    (Vòng review ngoài 3, G-FE3.)
         */
        if (range.from.isAfter(range.to)) {
            message.error('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc')
            /*
             * ⚠️ PHẢI tắt spinner. `loading` khởi tạo `true` và `useEffect` lúc mount gọi hàm này,
             *    nên mở màn bằng một URL có `from > to` mà không tắt là `<Spin>` + overlay của
             *    `Table` quay VĨNH VIỄN, và nút "So sánh kỳ trước" (`disabled={loading}`) chết
             *    theo. Đo: 0 lượt gọi API, `.ant-spin-spinning` = **2** (chính `<Spin>` và overlay
             *    của `Table`, đúng hai thứ câu trên vừa kể). Bản trước của dòng này ghi `= 1` —
             *    đó là số đo TRƯỚC khi qua biên `delay={200}` của `<Spin>`, tức đo quá sớm.
             *    Bản trước nữa có `setLoading(false)` ở đây; lượt vá G-FE3 dời `setLoading(true)`
             *    xuống dưới và đánh rơi nó. (Vòng review 4, M10 · số đo sửa ở vòng 5, A4-F8.)
             */
            setLoading(false)

            return
        }

        const runId = runIdRef.current + 1

        runIdRef.current = runId
        lastRequestedRef.current = range
        setLoading(true)
        setError(null)

        /*
         * Đổi bộ lọc thì GẬP mọi dòng đang mở. Khoá dòng là tài khoản/công đoạn của lượt TRƯỚC, nên
         * giữ lại là hai cột metadata (240px) kẹt ở trạng thái hiện trong khi không dòng nào thật
         * sự mở — người dùng không có cách nào đóng chúng ngoài việc mở rồi đóng một dòng bất kỳ.
         * (Vòng review 2, G-R6.)
         *
         * ⚠️ `keepExpanded` tồn tại vì `fetchReport` KHÔNG phải lúc nào cũng là "đổi bộ lọc". Hai
         *    trong sáu call site gọi lại API trên ĐÚNG bộ lọc hiện tại: mở khối so sánh kỳ trước
         *    (chỉ thêm cờ `comparePrevious`) và bấm "Thử lại" sau lỗi mạng. Ở đó tập dữ liệu không
         *    đổi nên khoá dòng vẫn hợp lệ, mà bản trước vẫn đóng sập mọi dòng người dùng vừa mở ra
         *    để đối chiếu. (Vòng review 7, #4.)
         */
        if (!keepExpanded) setExpanded({})

        try {
            /*
             * ⚠️ Biên NGÀY VN, không phải `toISOString()` của mốc địa phương. Service chỉ tính
             *    ngày nằm TRỌN trong khoảng (BR-2.1) — xem `vnDayRangeIso`, nó ghi lại ca đã hỏng.
             */
            const vnRange = vnDayRangeIso(range.from, range.to)

            const data = await (byStage ? getOutputReportByStage : getOutputReport)({
                from: vnRange.from,
                to: vnRange.to,
                actors: range.actors,
                stageCodes: range.stageCodes,
                /*
                 * ⚠️ `compareOverride` tồn tại vì `setCompareOpen` là bất đồng bộ: handler mở khối
                 *    gọi `fetchReport` ngay sau khi set, mà closure của nó vẫn giữ `compareOpen`
                 *    CŨ (false) — không có tham số này thì lượt gọi lúc mở khối không hề mang cờ,
                 *    và khối vừa mở ra đứng im báo "chưa có kỳ trước".
                 */
                comparePrevious: (compareOverride ?? compareOpen) || undefined,
            })

            // Lượt cũ về muộn thì VỨT, đừng đè lên kết quả của bộ lọc đang hiện.
            if (runIdRef.current !== runId) return

            setReport(data)
            setApplied(range)
        } catch (e) {
            if (runIdRef.current === runId) setError(e)
        } finally {
            if (runIdRef.current === runId) setLoading(false)
        }
    }, [from, to, actors, stageCodes, byStage, compareOpen])

    /*
     * Mở khối so sánh = gọi lại API. Không có đường nào khác lấy được số kỳ trước, và gọi lại ngay
     * tại handler thay vì qua effect để không chạy thêm một lượt lúc đóng khối lại.
     */
    const toggleCompare = useCallback(() => {
        const next = !compareOpen

        setCompareOpen(next)
        /*
         * Chỉ gọi lại khi MỞ ra. Đóng khối lại mà vẫn gọi là tốn một lượt đọc để bỏ đi dữ liệu.
         *
         * ⚠️ Truyền `applied` chứ không để `fetchReport` tự dựng range từ control đang hiển thị.
         *    Người dùng đổi ô "Tài khoản" mà CHƯA bấm Áp dụng rồi mở khối so sánh thì dữ liệu đổi
         *    theo bộ lọc chưa áp — trong khi `keepExpanded` giữ nguyên dòng của bộ lọc CŨ, nên hai
         *    cột metadata kẹt ở trạng thái hiện và bảng nói về một bộ lọc không ai bấm.
         *    (Vòng review ngoài 3, G-R6b.)
         */
        if (next) fetchReport(applied, true, true)
    }, [applied, compareOpen, fetchReport])

    /*
     * Bấm một tài khoản ở khối Tổng quan hoặc ở ma trận = LỌC về đúng người đó, không mở drawer mới.
     *
     * Lọc chứ không mở drawer vì màn này đã có sẵn đường khoan sâu ở từng dòng; thêm một lối vào
     * thứ hai với hành vi khác là hai đường đi tới cùng một chỗ mà người dùng phải học cả hai.
     */
    const selectActor = useCallback((actor) => {
        setFilter({actors: [actor]})
        fetchReport({actors: [actor]})
    }, [setFilter, fetchReport])

    useEffect(() => {
        // Danh sách công đoạn lấy từ Cấu hình chỉ số tiêu chuẩn, KHÔNG hardcode trong mã nguồn
        // (B-AC-04): thêm công đoạn mới ở M16 thì bộ lọc tự có thêm mục.
        getStages().then(setStageOptions).catch(() => setStageOptions([]))
        fetchReport()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Chuẩn hoá + dàn phẳng nằm ở `helpers/reportRows.js` để TEST ĐƯỢC — xem docblock ở đó.
    const groups = useMemo(() => groupsOf(report, byStage), [report, byStage])

    /*
     * Danh sách tài khoản cho ô lọc. Ở màn gộp theo công đoạn, tài khoản nằm ở tầng CON nên phải gom
     * lên và bỏ trùng — một tài khoản làm ba công đoạn thì xuất hiện ba lần.
     */
    const actorOptions = useMemo(
        () => (byStage
            ? [...new Set(groups.flatMap((group) => group.children.map((child) => child.actor)))].sort()
            : groups.map((group) => group.key)),
        [groups, byStage]
    )

    const openDropped = async () => {
        try {
            const shown = applied || {from, to}
            const vnRange = vnDayRangeIso(shown.from, shown.to)
            const result = await getOutputDropped({from: vnRange.from, to: vnRange.to})

            setDropped(result.data || [])
        } catch (e) {
            message.error('Không tải được danh sách dòng bị loại')
        }
    }

    const goDetail = (actor, stageCode, only) => {
        // `|| ''` cho cả ba: dòng cha ở màn theo công đoạn truyền `undefined` cho `actor`, mà ghi
        // `undefined` vào state bộ lọc thì `buildUrlSearch` xoá khoá còn state giữ `undefined` —
        // hai bên lệch nhau.
        setDetailFilter({
            detailOpen: '1',
            detailActor: actor || '',
            detailStageCode: stageCode || '',
            detailOnly: only || '',
            // Dọn khoảng thời gian RIÊNG của lượt khoan sâu trước. Con số vừa bấm thuộc khoảng đang
            // áp dụng của báo cáo, nên modal phải mở theo khoảng đó; giữ lại `detailFrom`/`detailTo`
            // cũ thì modal query một khoảng khác hẳn mà nhãn của nó lại tự nhất quán — không có gì
            // báo động, người dùng chỉ thấy "bấm 20 mà chi tiết ra 0 dòng".
            detailFrom: '',
            detailTo: '',
        })
    }

    const goSuspect = (actor, stageCode) => {
        setDetailFilter({suspectOpen: '1', suspectActor: actor || '', suspectStageCode: stageCode || ''})
    }

    /** Con số bấm được. Giá trị 0 hiện màu xám và KHÔNG bấm được (B-AC-09). */
    const clickableNumber = (value, onClick) => {
        if (!value) return <span className="VcZero">{formatInt(value)}</span>

        return (
            <button type="button" className="VcClickableNumber" onClick={onClick}>
                {formatInt(value)}
            </button>
        )
    }

    const rows = useMemo(
        () => rowsOf(groups, expanded, ((report && report.thresholds) || {}).dataCoverage || 0.9),
        [groups, report, expanded]
    )

    /*
     * Tiêu đề cột: nhãn NGẮN trên bảng, tên ĐẦY ĐỦ mở đầu tooltip.
     *
     * ⚠️ Nhãn ngắn là để bảng bớt cuộn ngang — dữ liệu ở đây là số vài ký tự, mà `width` của cột
     *    lại được chọn cho vừa tiêu đề ("Chưa tính được giờ — ĐM · file in" chiếm 180px để hiện
     *    `0 · 0`). Rút nhãn mà KHÔNG giảm `width` thì không được gì (bảng đặt `scroll.x` nên antd
     *    chạy `table-layout: fixed`, chữ ngắn lại chỉ để thừa chỗ); giảm `width` mà không rút nhãn
     *    thì tiêu đề xuống ba dòng. Hai việc phải đi cùng nhau.
     *
     * ⚠️ Và tooltip PHẢI mở đầu bằng tên đầy đủ. Nhãn ngắn là viết tắt, nên rê chuột lên "Nghi
     *    ngờ" mà đọc một đoạn không nhắc lại tên chỉ số thì người dùng không nối được nó với mục
     *    nào trong màn "Giải thích chỉ số" — ở đó vẫn là tên đầy đủ, và đó là chủ đích.
     */
    const header = (key, label) => {
        const full = OUTPUT_LABELS[key]
        const title = full && full !== label ? `${full} — ${OUTPUT_TOOLTIPS[key]}` : OUTPUT_TOOLTIPS[key]

        return (
            <Tooltip title={title}>
                <span className="VcHasTooltip">{label}</span>
            </Tooltip>
        )
    }

    const columns = [
        {
            title: '',
            key: 'caret',
            width: 40,
            fixed: 'left',
            render: (text, row) => (row.type === 'actor'
                ? (row.isOpen ? <CaretDownOutlined/> : <CaretRightOutlined/>)
                : ''),
        },
        {
            title: header('actor', 'Tài khoản'),
            key: 'actor',
            /*
             * 150 là bề rộng CHẤP NHẬN XUỐNG DÒNG, không phải bề rộng đủ cho tên dài nhất — muốn
             * mọi tên nằm gọn một dòng thì cột phải 200 (`↳ huyennn1@foobla.com` = 157px + 26px
             * thụt đầu dòng + 16px padding), tức ngốn 50px của một bảng vốn đã phải cuộn ngang.
             * Đo trên 77 nhãn dòng con thật của tháng 10: cột 150 → 28 nhãn xuống 2 dòng, cột 170
             * → 5 nhãn (tức bản 170 trước đây CŨNG đã xuống dòng, chỉ là ít hơn), cột 200 → 0.
             *
             * `AccountLabel` + `.VcActorCell` là thứ làm cho việc xuống dòng đó ĐỌC ĐƯỢC; thiếu
             * chúng thì chỗ ngắt rơi vào giữa tên (`huyennn1@foobla.c` / `om`) hoặc chữ tràn khỏi ô.
             */
            width: 150,
            fixed: 'left',
            className: 'VcActorCell',
            render: (text, row) => {
                // Gộp theo tài khoản: dòng cha LÀ tài khoản. Gộp theo công đoạn: dòng cha chỉ đếm
                // được bao nhiêu tài khoản, còn tên tài khoản rơi xuống dòng con.
                if (row.type === 'actor') {
                    return byStage
                        ? <span className="VcMuted"><AccountLabel value={row.group.actorLabel}/></span>
                        : <strong><AccountLabel value={row.group.actorLabel}/></strong>
                }

                if (row.type === 'stage') {
                    /*
                     * `&nbsp;` chứ không phải dấu cách thường: dấu cách là điểm ngắt dòng, nên cột
                     * hẹp hơn tên sẽ đẩy TOÀN BỘ tên xuống dòng hai và để mũi tên `↳` đứng một
                     * mình trên dòng một — đo được ở bản 170px hiện tại với ba tài khoản email.
                     */
                    return byStage
                        ? <span className="VcIndent">↳&nbsp;<AccountLabel value={row.actor}/></span>
                        : <span className="VcIndent">↳</span>
                }

                return ''
            },
        },
        {
            title: header('stage', 'Công đoạn'),
            key: 'stage',
            width: 145,
            render: (text, row) => {
                if (row.type === 'actor') {
                    return byStage
                        ? <strong>{row.group.stageLabel}</strong>
                        : <span className="VcMuted">{row.group.stageLabel}</span>
                }

                // Gộp theo công đoạn thì tên công đoạn đã nằm ở dòng cha — lặp lại ở từng dòng con
                // chỉ làm bảng ồn.
                if (row.type === 'stage') return byStage ? '' : row.stage.name

                return null
            },
        },
        /*
         * ⚠️ Hai cột metadata này CHỈ hiện khi có dòng được mở — xem `anyExpanded`.
         *
         *    Chúng mô tả thuộc tính CÔNG ĐOẠN (đếm theo gì, tính giờ theo gì), mà dòng cha là dòng
         *    gộp nên không có công đoạn nào để mô tả: chúng in dấu gạch ngang cho đúng những hàng
         *    người dùng nhìn đầu tiên. 210px cho một cột toàn dấu gạch, trên một bảng vốn đã phải
         *    cuộn ngang.
         */
        {
            title: header('countUnit', 'Đơn vị'),
            key: 'countUnit',
            width: 100,
            render: (text, row) => (row.type === 'stage'
                ? <CountUnitTag countUnit={row.stage.countUnit} variant="m18"/>
                : <span className="VcZero">{DASH}</span>),
        },
        {
            title: header('timeBasis', 'Tính giờ'),
            key: 'timeBasis',
            /*
             * 140 chỉ đủ cho CÁI THẺ — tên đại lượng (`cutting_length`, 101px) xuống dòng dưới nó,
             * có chủ đích. Cặp đó nằm cùng một dòng đòi 240px. Xem `.VcTimeBasis--stacked`.
             */
            width: 140,
            render: (text, row) => (row.type === 'stage'
                ? (
                    <TimeBasisTag
                        timeBasis={row.stage.timeBasis}
                        detectField={row.stage.detectField}
                        variant="m18"
                        stacked
                    />
                )
                : <span className="VcZero">{DASH}</span>),
        },
        {
            title: header('output', 'Sản lượng'),
            key: 'output',
            align: 'right',
            width: 60,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                return clickableNumber(data.output, () => goDetail(
                    row.drillActor,
                    row.drillStageCode
                ))
            },
        },
        {
            title: header('distinctUnits', 'SP khác nhau'),
            key: 'distinctUnits',
            align: 'right',
            width: 65,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                return clickableNumber(data.distinctUnits, () => goDetail(
                    row.drillActor,
                    row.drillStageCode,
                    'distinct'
                ))
            },
        },
        {
            title: header('redoCount', 'Làm lại'),
            key: 'redoCount',
            align: 'right',
            width: 60,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                const link = clickableNumber(data.redoCount, () => goDetail(
                    row.drillActor,
                    row.drillStageCode,
                    'redo'
                ))

                // Con số tuyệt đối một mình không so sánh được: 373 lần làm lại của một tài khoản
                // chạy 14.000 sản phẩm khác hẳn 373 lần của tài khoản chạy 900. Chỉ hiện phần trăm
                // khi KHÁC 0 — bằng 0 thì "0 · 0,0%" chỉ là nhiễu, cả cột đầy những dòng như vậy.
                if (!data.redoCount || !data.output) return link

                return (
                    <div className="VcRedoCell">
                        {link}
                        <div className="VcRedoCell__percent">{percentOf(data.redoCount, data.output)}</div>
                    </div>
                )
            },
        },
        {
            title: header('suspectCount', 'Nghi ngờ'),
            key: 'suspectCount',
            align: 'right',
            width: 50,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                return clickableNumber(data.suspectCount, () => goSuspect(
                    row.drillActor,
                    row.drillStageCode
                ))
            },
        },
        {
            title: header('standardHours', 'Giờ định mức'),
            key: 'standardHours',
            align: 'right',
            width: 85,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                // Không bấm được vì không phải số đếm. Giờ định mức = 0 vẫn hiện 0,00 h, không để
                // trống (B.4).
                const value = `${formatHours(data.standardHours, 2)} h`

                return row.type === 'actor' ? <strong>{value}</strong> : value
            },
        },
        {
            title: header('notComputed', 'Chưa tính giờ'),
            key: 'notComputed',
            align: 'right',
            width: 80,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                // Hai số riêng, mỗi số mở ra một danh sách khác nhau, hai bộ phận xử lý khác nhau.
                return (
                    <span>
                        {clickableNumber(data.noStandardOutput, () => goDetail(
                            row.drillActor,
                            row.drillStageCode,
                            'no_standard'
                        ))}
                        {' · '}
                        {clickableNumber(data.noFileDataOutput, () => goDetail(
                            row.drillActor,
                            row.drillStageCode,
                            'no_file_data'
                        ))}
                    </span>
                )
            },
        },
        {
            title: header('hasStandard', 'Độ phủ'),
            key: 'coverage',
            align: 'right',
            width: 70,
            render: (text, row) => {
                const data = row.type === 'actor' ? row.group.summary : row.type === 'stage' ? row.stage : null

                if (!data) return null

                return (
                    <span>
                        <PercentTag ratio={data.hasStandard} kind="standard"/>
                        {' · '}
                        <PercentTag ratio={data.hasFileData} kind="fileData"/>
                    </span>
                )
            },
        },
    ]

    /**
     * Dòng cảnh báo gộp toàn bộ cột (§B.2 ④). Dùng colSpan của antd: ô đầu chiếm hết chiều rộng,
     * các ô còn lại colSpan = 0 để không render thẻ <td> nào.
     */
    const warningText = (row) => (
        <span>
            {row.lowStandard && (
                <span>
                    Thiếu định mức {formatInt(row.group.summary.noStandardOutput)} sản lượng — chuyển{' '}
                    {OWNERS.standard} bổ sung.{' '}
                </span>
            )}
            {row.lowFileData && (
                <span>
                    Thiếu dữ liệu file in {formatInt(row.group.summary.noFileDataOutput)} sản lượng —
                    chuyển {OWNERS.fileData} xử lý.{' '}
                </span>
            )}
            Số liệu vẫn được tính đủ cho phần đã có dữ liệu.
        </span>
    )

    /*
     * Gộp theo công đoạn thì "Công đoạn" phải đứng TRƯỚC "Tài khoản" — cột đầu tiên là trục nhóm,
     * đọc bảng từ trái sang phải mới thuận. Đổi chỗ chứ không khai hai mảng cột: 10 cột còn lại
     * giống hệt nhau, tách ra là chép.
     *
     * ⚠️ `fixed: 'left'` phải đi THEO cột đứng trước, không dính cứng vào cột "Tài khoản". antd đòi
     *    các cột ghim trái nằm LIỀN NHAU ở đầu bảng; để nguyên `fixed` trên "Tài khoản" sau khi đẩy
     *    "Công đoạn" lên trước là chèn một cột không ghim vào giữa nhóm ghim — bảng vỡ bố cục và
     *    antd cảnh báo ở console.
     */
    /*
     * Nhóm cột HIỆU SUẤT nối vào SAU mười hai cột đang có, không chen vào giữa (bản mẫu chốt: "12
     * cột hiện có và thứ tự cột" nằm trong danh sách GIỮ NGUYÊN). Hai màn dùng hai bộ cột khác nhau
     * vì chúng trả lời hai câu hỏi khác nhau — màn theo người cần hạng và xu hướng của từng người,
     * màn theo công đoạn cần phân bố của cả công đoạn.
     *
     * Định nghĩa cột nằm ở `efficiencyColumns.js`; ở đây chỉ nối. Đặt chúng trong file này là trang
     * vượt 1.400 dòng và mỗi lần sửa một cột phải cuộn qua mười một cột khác.
     */
    const efficiencyThresholds = report && report.thresholds ? report.thresholds.grades : null
    /*
     * Tra tín hiệu của một công đoạn theo mã. Dựng một `Map` thay vì `find` trong `render`: cột
     * này chạy một lần mỗi dòng, và `find` tuyến tính trong render là O(công đoạn × dòng).
     */
    const stageSignalOf = (() => {
        const byCode = new Map(((report && report.stageSignals) || []).map((item) => [item.stageCode, item.signal]))

        return (stageCode) => byCode.get(stageCode) || null
    })()
    /*
     * Có dòng nào đang mở không. Hai cột metadata bám vào đây — xem docblock chỗ khai báo chúng.
     *
     * ⚠️ `Object.values(...).some(Boolean)` chứ không phải `Object.keys(...).length`: `setExpanded`
     *    ghi `false` khi người dùng ĐÓNG một dòng chứ không xoá khoá, nên đếm khoá là mở rồi đóng
     *    lại vẫn coi như đang mở, và hai cột kia không bao giờ biến mất nữa.
     */
    const anyExpanded = anyExpandedOf(expanded)

    const columnsWithEfficiency = [
        ...columns.filter((column) => anyExpanded || !META_COLUMN_KEYS.includes(column.key)),
        ...(byStage
            ? stageEfficiencyColumns({header, thresholds: efficiencyThresholds, signalOf: stageSignalOf})
            : actorEfficiencyColumns({header, thresholds: efficiencyThresholds}))
    ]

    const orderedColumns = (() => {
        if (!byStage) return columnsWithEfficiency

        const actorIndex = columnsWithEfficiency.findIndex((column) => column.key === 'actor')
        const stageIndex = columnsWithEfficiency.findIndex((column) => column.key === 'stage')

        if (actorIndex < 0 || stageIndex < 0) return columnsWithEfficiency

        const swapped = [...columnsWithEfficiency]

        swapped[actorIndex] = {...columnsWithEfficiency[stageIndex], fixed: 'left'}
        swapped[stageIndex] = {...columnsWithEfficiency[actorIndex], fixed: undefined}

        return swapped
    })()

    const mergedColumns = orderedColumns.map((column, index) => ({
        ...column,
        render: (text, row, rowIndex) => {
            if (row.type === 'warning') {
                return index === 0
                    ? {children: warningText(row), props: {colSpan: orderedColumns.length}}
                    : {children: null, props: {colSpan: 0}}
            }

            return column.render ? column.render(text, row, rowIndex) : text
        },
    }))

    const totals = (report && report.totals) || {}

    /**
     * Bóc "Số lần làm lại" theo trục phụ.
     *
     * Tính ở FE thay vì thêm field mới ở API: response đã mang đủ `output` và `redoCount` ở từng
     * (tài khoản × công đoạn), nên đây chỉ là gộp lại — thêm một vòng round-trip cho dữ liệu đã nằm
     * sẵn trong tay là phí.
     *
     * Trục BÓC TÁCH của thẻ "Số lần làm lại" luôn là trục KHÔNG dùng để gộp bảng.
     *
     * Màn gộp theo tài khoản hỏi "làm lại ở công đoạn nào"; màn gộp theo công đoạn đã biết công đoạn
     * rồi, câu còn lại là "ai làm lại". Bóc theo đúng trục đang gộp thì bảng phía dưới đã trả lời
     * sẵn, popover thành thừa.
     */
    const breakdownLabel = byStage ? 'nhân viên' : 'công đoạn'

    const redoBreakdown = useMemo(() => {
        const byKey = new Map()

        // Đi qua `groups.children` chứ không qua `report.actors`: phần tử con giống nhau ở cả hai
        // cách nhóm, nên một vòng lặp chạy đúng cho cả hai màn.
        for (const group of groups) {
            for (const child of group.children) {
                const key = byStage ? child.actor : child.stageCode
                const label = byStage ? child.actor : child.name || child.stageCode
                const acc = byKey.get(key) || {key, name: label, redoCount: 0, output: 0}

                acc.redoCount += child.redoCount || 0
                acc.output += child.output || 0
                byKey.set(key, acc)
            }
        }

        // Nhiều nhất lên đầu, và bỏ hẳn công đoạn không có lần làm lại nào — danh sách này để trả
        // lời "đi sửa ở đâu", nên những dòng bằng 0 chỉ làm loãng.
        return [...byKey.values()].filter((item) => item.redoCount > 0).sort((a, b) => b.redoCount - a.redoCount)
    }, [groups, byStage])
    const thresholds = (report && report.thresholds) || {}

    /*
     * Chú thích của thẻ Tổng sản lượng công đoạn phải GỌI ĐÚNG ĐƠN VỊ.
     *
     * ⚠️ Nhãn cũ cứng là "sản phẩm khác nhau", nhưng `distinctUnits` đổi nghĩa theo `countUnit` của
     *    công đoạn: công đoạn đếm theo ĐƠN thì nó là số ĐƠN. Lọc riêng Đóng thùng ngày 25/09 màn hiện
     *    "1.466 sản phẩm khác nhau" trong khi 1.466 là số đơn và số sản phẩm thật là 1.975 — sai đơn
     *    vị, không sai số, nên không ai phát hiện cho tới khi đi đối chiếu với báo cáo khác.
     *
     * Bộ `countUnit` lấy từ CHÍNH nguồn mà cột "Đơn vị đếm" đang đọc (`groups[].children[]`, tức
     * object tài khoản × công đoạn mà `apiProductionPlanning.getOutputReportByStage` mô tả), chứ
     * không đoán theo bộ lọc: người dùng có thể chọn nhiều công đoạn lẫn lộn hai kiểu đếm, và khi đó
     * `distinctUnits` là tổng của hai đơn vị khác nhau — một con số không gọi tên được.
     *
     * ⚠️ ĐỪNG đọc từ `report.rows`. Response không có khoá đó (hợp đồng là `stages[]` hoặc
     *    `actors[]`), nên `countUnits` sẽ luôn rỗng, mọi nhánh dưới thành code chết, và chú thích
     *    rơi về một field cũng không tồn tại — thẻ mất hẳn số mà vẫn giữ nguyên nhãn sai. Hỏng im
     *    lặng, đúng thứ đoạn này sinh ra để diệt.
     */
    /*
     * Cột của ma trận = công đoạn THỰC SỰ xuất hiện trong tập đang hiện, không phải cả danh mục.
     *
     * Lấy cả danh mục thì ma trận có 9 cột mà 5 cột trống hoàn toàn, và người đọc mất thời gian dò
     * xem mình có bỏ sót gì ở mấy cột rỗng đó không.
     */
    const matrixStages = useMemo(() => {
        const seen = new Map()

        ;((report && report.actors) || []).forEach((entry) => {
            (entry.stages || []).forEach((stage) => {
                if (!seen.has(stage.stageCode)) seen.set(stage.stageCode, stage.name || stage.stageCode)
            })
        })

        return [...seen].map(([code, name]) => ({code, name}))
    }, [report])

    const caption = outputCaption(totals, groups)
    const efficiencyTotals = report ? report.efficiency : null
    const pureNote = pureEfficiencyNoteOf(efficiencyTotals)
    const previousRangeLabel = previousRangeLabelOf(efficiencyTotals)
    /*
     * Bộ lọc ĐÃ ÁP DỤNG — cùng nguồn với bảng bên dưới. Mọi thứ MÔ TẢ dữ liệu đang hiện phải đọc
     * `applied`, không đọc state của ô Select: hai thứ đó lệch nhau suốt quãng từ lúc người dùng
     * chọn tới lúc họ bấm Áp dụng.
     */
    const appliedFilters = applied || {actors: [], stageCodes: []}
    /** Lọc đúng MỘT công đoạn thì ma trận xếp theo công đoạn đó và tô đậm cột đó (FR-007). */
    const matrixFocusStage = (appliedFilters.stageCodes || []).length === 1
        ? appliedFilters.stageCodes[0]
        : null
    /** Chênh lệch hiệu suất chung so kỳ trước, dạng phân số — `null` khi chưa nạp kỳ trước. */
    const previousDelta = efficiencyTotals && efficiencyTotals.previous
        ? deltaOf(efficiencyTotals.efficiency, efficiencyTotals.previous.efficiency)
        : null
    /*
     * Công đoạn có trung vị lệch khỏi 60–130% — nhiều khả năng sai ở ĐỊNH MỨC chứ không ở người.
     * Chỉ màn theo công đoạn có `stages[]`; màn theo người không dựng được danh sách này, và đó là
     * lý do bản mẫu chỉ đặt dải cảnh báo kèm nút "mở Chỉ số tiêu chuẩn" ở màn theo công đoạn.
     */
    const rateWarnings = report ? rateSanityWarningsOf(report.stageSignals) : []
    /*
     * Công đoạn có độ phủ định mức dưới ngưỡng — hiệu suất ở đó phần lớn là ƯỚC TÍNH (BR-8.3).
     * Ngưỡng lấy từ service (`thresholds.dataCoverage`), không hardcode 90%: PO chỉnh được nó, và
     * một màn hình dạy người dùng một ngưỡng khác ngưỡng đang chấm là tệ hơn không nói gì.
     */
    const noEfficiencyReason = noEfficiencyReasonOf(report)
    const lowCoverageStages = lowCoverageStagesOf(groups, thresholds.dataCoverage)
    const milestoneNotes = milestoneStagesOf(report && report.stageSignals)
    /*
     * ⚠️ Tính ở MỖI lượt render, cố ý không `useMemo`: `freshnessOf` đọc `Date.now()` để ra số phút
     *    đã cũ, nên nhớ lại kết quả là đóng băng một con số vốn phải trôi. Phép tính là một lần
     *    `new Date()` + một phép trừ.
     */
    const freshness = freshnessOf(report && report.dataFreshAs)
    const gradeEligibility = report && report.thresholds ? report.thresholds.gradeEligibility : null

    /** Mở màn Giờ làm thực tế ở ĐÚNG khoảng đang lọc — không bắt người dùng chọn lại ngày. */
    const goActualHours = () => history.push(
        /*
         * ⚠️ `from`/`to` là hai state RIÊNG, không nằm trong `filters` — xem docblock chỗ khai báo
         *    chúng. Truyền `filters` vào đây là gọi `.clone()` trên một plain object: nút này ném
         *    TypeError giữa handler và người dùng mất cả trang báo cáo.
         */
        `${ROUTES.ACTUAL_HOURS}?from=${encodeURIComponent(rangeStartOf(from).format('YYYY-MM-DD'))}`
        + `&to=${encodeURIComponent(rangeEndOf(to).format('YYYY-MM-DD'))}`
    )

    /*
     * Dải thẻ KPI dựng ở `kpiCards.js` — xem docblock ở đó, việc tách ra là một ràng buộc thật của
     * repo này chứ không phải sở thích trình bày.
     */
    const kpis = kpiCards({
        report,
        totals,
        caption,
        efficiencyTotals,
        efficiencyThresholds,
        pureNote,
        noEfficiencyReason,
        breakdownLabel,
        redoBreakdown,
        previousDelta,
        compareOpen,
    })

    const handleExport = async () => {
        setExporting(true)

        try {
            // Phải tự dựng link tải: `await` không thôi thì blob bị bỏ đi mà vẫn báo thành công.
            const shown = applied || {from, to, actors, stageCodes}
            const vnRange = vnDayRangeIso(shown.from, shown.to)
            const blob = await exportOutputReport({
                from: vnRange.from,
                to: vnRange.to,
                actors: shown.actors,
                stageCodes: shown.stageCodes,
            })
            const range = `${shown.from.format('YYYYMMDD')}-${shown.to.format('YYYYMMDD')}`

            downloadBlob(blob, `kiem-soat-san-luong-${range}-${exportStamp()}.xlsx`)
            message.success('Đã tải file. Tên file gồm cả khoảng thời gian và thời điểm xuất.')
        } catch (e) {
            // Lỗi đã được interceptor trải phẳng, đọc qua helper — xem helpers/apiError.js.
            if (errorCode(e) === 'M18_E003') {
                Modal.warning({
                    title: 'Vượt giới hạn dòng khi xuất',
                    content: 'Thu hẹp khoảng thời gian hoặc chọn bớt tài khoản rồi xuất lại.',
                })
            } else {
                message.error(errorMessage(e) || 'Xuất file thất bại. Vui lòng thử lại')
            }
        } finally {
            setExporting(false)
        }
    }

    if (error) {
        const code = errorCode(error)

        // TT-03 / TT-04 — hộp lỗi thay cho toàn bộ nội dung, KHÔNG hiện bảng rỗng, và ghi rõ ai là
        // người xử lý (G-01, G-02).
        if (code === 'M18_E005') {
            return (
                <div className="VcPage container-fluid">
                    <Notice level="error">
                        <div>Chưa chạy được báo cáo: chưa có dữ liệu định mức.</div>
                        <div className="VcNoticeSub">Người xử lý: {OWNERS.standard}.</div>
                        <Button className="mt-2" onClick={() => history.push(ROUTES.STANDARD_TIME_STAGES)}>
                            Mở Cấu hình chỉ số tiêu chuẩn
                        </Button>
                    </Notice>
                </div>
            )
        }

        if (code === 'M18_E006') {
            return (
                <div className="VcPage container-fluid">
                    <Notice level="error">
                        <div>
                            Cấu hình chỉ số tiêu chuẩn chưa khai báo mã thao tác cho các công đoạn, hệ
                            thống không biết mỗi dòng log thuộc công đoạn nào.
                        </div>
                        <div className="VcNoticeSub">
                            Người xử lý: IE + Dev Lead. Bảng ánh xạ thao tác thuộc chức năng Cấu hình chỉ
                            số tiêu chuẩn, không khai báo riêng ở báo cáo này.
                        </div>
                    </Notice>
                </div>
            )
        }

        return (
            <div className="VcPage container-fluid">
                {/*
                  * Thử lại ĐÚNG lượt vừa hỏng — xem `lastRequestedRef`. KHÔNG dùng `applied`
                  * (bộ lọc thành công gần nhất) và cũng không `undefined` (control đang hiển thị,
                  * mà màn lỗi `return` sớm nên thẻ bộ lọc không render — hai thứ có thể lệch nhau).
                  */}
                <LoadError
                    error={error}
                    onRetry={() => fetchReport(lastRequestedRef.current ?? undefined, undefined, true)}
                />
            </div>
        )
    }

    return (
        <div className="VcPage VcOutputReportPage container-fluid">
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">
                        Sản lượng &amp; giờ định mức theo {byStage ? 'công đoạn' : 'nhân viên'}
                    </h1>
                    <ReportSubtitle
                        from={(applied || {from}).from}
                        to={(applied || {to}).to}
                        computedAt={report ? report.computedAt : null}
                        freshness={freshness}
                    />
                </div>
                <div className="VcPageActions">
                    <Button onClick={() => setDetailFilter({glossaryOpen: '1'})}>Giải thích chỉ số</Button>
                    <Button type="primary" loading={exporting} onClick={handleExport}>Xuất Excel</Button>
                </div>
            </div>

            <div className="VcFilterCard">
                <div className="VcFilterRow">
                    <div className="VcFilterItem">
                        <label>Khoảng thời gian</label>
                        {/*
                          * MỘT ô chọn khoảng thay cho hai ô rời.
                          *
                          * Hai ô rời cho phép chọn "từ" sau "đến" — một trạng thái vô nghĩa mà không
                          * gì chặn được, và người dùng chỉ biết mình sai khi báo cáo trả về rỗng.
                          * `RangePicker` không cho dựng ra trạng thái đó, đồng thời có sẵn chỗ đặt
                          * các khoảng dùng sẵn.
                          *
                          * ⚠️ KHÔNG có `showTime` ở màn này, cố ý khác M17. Báo cáo chỉ tính NGÀY
                          *    TRỌN (BR-2.1), nên một ô giờ là lời mời vào đúng trạng thái mà báo
                          *    cáo không đáp ứng được: chọn 08:00→18:00 là khoảng không chứa ngày
                          *    trọn nào, và cả 6 cột hiệu suất trống. Không hỏi thì không phải trả lời.
                          */}
                        <DatePicker.RangePicker
                            format="DD/MM/YYYY"
                            allowClear={false}
                            ranges={datePresets}
                            value={[from, to]}
                            onChange={(value) => {
                                const [nextFrom, nextTo] = value || []

                                if (nextFrom && nextTo) {
                                    setFrom(rangeStartOf(nextFrom))
                                    setTo(rangeEndOf(nextTo))
                                }
                            }}
                            style={{width: 350}}
                        />
                    </div>
                    <div className="VcFilterItem">
                        <label>Tài khoản</label>
                        <Select
                            mode="multiple"
                            placeholder="Tất cả tài khoản"
                            value={actors}
                            onChange={(value) => setFilter({actors: value})}
                            style={{minWidth: 200}}
                        >
                            {actorOptions.map((actor) => <Option key={actor} value={actor}>{actor}</Option>)}
                        </Select>
                    </div>
                    <div className="VcFilterItem">
                        <label>Công đoạn</label>
                        <Select
                            mode="multiple"
                            placeholder="Tất cả công đoạn"
                            value={stageCodes}
                            onChange={(value) => setFilter({stageCodes: value})}
                            style={{minWidth: 220}}
                        >
                            {stageOptions.map((stage) => (
                                <Option key={stage.stageCode} value={stage.stageCode}>{stage.name}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="VcFilterItem VcFilterItem--action">
                        <Button type="primary" onClick={() => fetchReport()}>Áp dụng</Button>
                        <Button onClick={() => {
                            const nextFrom = moment().startOf('day')
                            const nextTo = moment().endOf('day')

                            setFrom(nextFrom)
                            setTo(nextTo)
                            setFilter({actors: [], stageCodes: []})
                            fetchReport({from: nextFrom, to: nextTo, actors: [], stageCodes: []})
                        }}
                        >
                            Đặt lại
                        </Button>
                    </div>
                </div>

                {/*
                  * UI-D07: số dòng log đã đọc và số dòng bị loại hiển thị NGAY dưới bộ lọc, không giấu
                  * trong nhật ký hệ thống — dòng bị loại nhiều bất thường là dấu hiệu bảng ánh xạ còn
                  * thiếu, phải nhìn thấy mới sửa được.
                  */}
                <div className="VcFilterFoot">
                    <span><strong>{formatInt(report && report.logsRead)}</strong> dòng log đã đọc</span>
                    <span>
                        <button type="button" className="VcClickableNumber" onClick={openDropped}>
                            {formatInt(report && report.droppedCount)}
                        </button>{' '}
                        dòng bị loại{' '}
                        <Tooltip title={OUTPUT_TOOLTIPS.droppedCount}>
                            <QuestionCircleOutlined/>
                        </Tooltip>
                    </span>
                    <span className="VcMuted">
                        Ngưỡng cảnh báo ghi nhận: {formatInt(thresholds.suspectLogCount)} lần · Ngưỡng chỉ
                        số dữ liệu: {formatPercent(thresholds.dataCoverage)}
                    </span>
                    {/*
                      * Ngưỡng xếp hạng ở CHÂN BỘ LỌC, không giấu trong tooltip: người đọc bảng cần
                      * biết "A nghĩa là gì" ngay cạnh cột Xếp hạng, không phải sau một lần rê chuột.
                      * Số lấy từ service — ngưỡng chỉnh được ở config, và một màn hình dạy ngưỡng
                      * khác ngưỡng đang chấm còn tệ hơn không nói gì.
                      */}
                    {efficiencyThresholds && (
                        <span className="VcMuted">
                            Xếp hạng: A ≥ {formatPercent(efficiencyThresholds.a, 0)} · B ≥{' '}
                            {formatPercent(efficiencyThresholds.b, 0)} · C ≥{' '}
                            {formatPercent(efficiencyThresholds.c, 0)}
                            {gradeEligibility && (
                                <>
                                    {' · Điều kiện: ≥ '}
                                    {gradeEligibility.minHours} giờ, ≥ {gradeEligibility.minDays} ngày, đã
                                    tính được giờ ≥ {formatPercent(gradeEligibility.minComputedRatio, 0)}
                                </>
                            )}
                        </span>
                    )}
                    {/*
                      * Số ngày-công CÓ SẢN LƯỢNG mà CHƯA NHẬP giờ — bấm được, mở thẳng màn Giờ
                      * làm thực tế ở đúng khoảng đang lọc. Một con số cảnh báo mà không có đường
                      * đi tới chỗ sửa là một con số người dùng học cách bỏ qua.
                      *
                      * ⚠️ "Thiếu giờ công" đúng theo bản mẫu M18 và plan §5, và nó khớp ĐÚNG vị từ
                      *    service đang đếm (`hours === undefined`, không gồm ngày đã khai 0). Đã
                      *    có một lượt đổi nhãn thành "chưa tính được hiệu suất" rồi phải lùi: câu
                      *    đó bao cả nhóm thiếu ĐỊNH MỨC (In/Cắt/Khắc), mà nhóm đó không nằm trong
                      *    con số — thợ Cắt cả tháng không tính được hiệu suất vẫn thấy banner 0.
                      *    Nhãn phải mô tả đúng cái con số đếm, không phải cái người đọc mong nó đếm.
                      */}
                    {/*
                      * ⚠️ `null` (lỗi đọc giờ làm) tự rơi khỏi `> 0`, và đó là ĐÚNG: lúc đó không
                      *    ai biết còn thiếu bao nhiêu ngày, nên nút "đi nhập bổ sung" không được
                      *    xuất hiện — dải đỏ ở trên đã bảo người dùng đừng nhập lại.
                      */}
                    {efficiencyTotals && efficiencyTotals.missingHourDays > 0 && (
                        <button type="button" className="VcClickableNumber VcWarning" onClick={goActualHours}>
                            Còn {formatInt(efficiencyTotals.missingHourDays)} ngày-công thiếu giờ công →
                        </button>
                    )}
                </div>
            </div>

            {/*
              * ⚠️ Dải thẻ KPI đứng TRÊN khối biểu đồ và trên bảng (BR-6.1). Bản trước đặt biểu đồ
              *    lên trước: người mở màn hình gặp một khối phân tích dài trước khi biết tổng
              *    của cả xưởng là bao nhiêu — đúng thứ tự ngược với cách đọc một báo cáo, và
              *    docblock của `TeamEfficiencyPanel` còn tự ghi là nó "nằm ngay dưới dải thẻ KPI".
              */}
            {/* Cùng lý do với màn Khối lượng & nhân lực: chỉ bảng có `loading` thì dải thẻ giữ
                nguyên số của lượt tải trước trong lúc gọi lại, đọc như thể bộ lọc vừa đổi không có
                tác dụng. `delay` để lượt gọi nhanh không kịp nháy. */}
            <Spin spinning={loading} delay={SPIN_DELAY_MS}>
                {/*
                  * Bỏ modifier `--five`: `.VcKpiStrip` mặc định ĐÃ là 6 cột, `--five` mới là cái
                  * ghi đè xuống 5. Thêm `--six` là khai một lớp không tồn tại và dải thẻ im lặng
                  * giữ nguyên 5 ô trong khi có 6 thẻ.
                  */}
                <div className="VcKpiStrip">
                    {kpis.map((card) => (
                        <div className={`VcKpiCard VcKpiCard--${card.tone || ''}`} key={card.label}>
                            <div className="VcKpiLabel">
                                {/*
                                  * Tra chú giải theo KHOÁ gắn trên từng thẻ, không so chuỗi nhãn.
                                  *
                                  * ⚠️ Bản trước so `card.label === 'Tổng sản lượng'`: đổi một chữ trong
                                  *    nhãn là tooltip im lặng rơi về nhánh cuối (`hasFileData`) — chú
                                  *    giải SAI chứ không mất, nên không ai phát hiện. Đúng lúc đổi nhãn
                                  *    thành "Tổng sản lượng công đoạn" thì bẫy này nổ.
                                  */}
                                <Tooltip title={OUTPUT_TOOLTIPS[card.tooltipKey]}>
                                    <span className="VcHasTooltip">{card.label}</span>
                                </Tooltip>
                            </div>
                            <div className="VcKpiValue">{card.value}</div>
                            {/*
                              * Thẻ nào có phần tách nhỏ thì phần chú thích bấm được để mở Popover. Không
                              * đổi cả thẻ thành nút: bốn thẻ còn lại không có gì để mở, một dải thẻ mà
                              * chỗ bấm được chỗ không sẽ khó đoán hơn là một liên kết nằm rõ một chỗ.
                              */}
                            {card.detail ? (
                                <Popover content={card.detail} trigger="click" placement="bottomLeft">
                                    <button type="button" className="VcKpiCaption VcKpiCaption--more">
                                        {card.caption} · xem theo {breakdownLabel}
                                    </button>
                                </Popover>
                            ) : (
                                <div className="VcKpiCaption">{card.caption}</div>
                            )}
                        </div>
                    ))}
                </div>
            </Spin>

            {/*
              * Khối Tổng quan đội ngũ — CHỈ ở màn theo nhân viên.
              *
              * ⚠️ Màn theo công đoạn không có tổng của từng người (chỉ có ô tài khoản × công đoạn),
              *    nên đếm hạng ở đó là đếm một người ba lần vì họ làm ba công đoạn. Việc tương
              *    đương ở màn kia là biểu đồ Hiệu suất theo công đoạn ngay dưới đây.
              */}
            {!byStage && efficiencyTotals && (
                <TeamEfficiencyPanel
                    actors={(report && report.actors) || []}
                    totals={efficiencyTotals}
                    thresholds={efficiencyThresholds}
                    open={compareOpen}
                    onToggle={toggleCompare}
                    loadingPrevious={loading && compareOpen}
                    onSelectActor={selectActor}
                />
            )}

            {/*
              * ⚠️ Nút so sánh kỳ trước phải có ở CẢ HAI màn. Trước đây `toggleCompare` chỉ được
              *    truyền cho `TeamEfficiencyPanel`, mà panel chỉ render khi `!byStage` — nên trên
              *    màn theo công đoạn cờ `comparePrevious` không bao giờ được gửi, `stageMedians`
              *    luôn rỗng, và toàn bộ lớp "kỳ trước" của biểu đồ (vệt nhạt, chú giải, cột chênh,
              *    phần kỳ trước trong tooltip) là code chết. Service vẫn tính và mang về đúng
              *    `stageMedians` cho màn kia, nơi không ai vẽ nó.
              */}
            {byStage && efficiencyThresholds && (
                <div className="VcCompareToggleRow">
                    {/* `Button type="link"` — cùng kiểu nút với nút so sánh của TeamEfficiencyPanel ở màn kia. */}
                    <Button type="link" size="small" onClick={toggleCompare} disabled={loading}>
                        {compareOpen ? 'Ẩn so sánh với kỳ trước' : 'So sánh với kỳ trước cùng độ dài'}
                    </Button>
                    {loading && compareOpen && <span className="VcMuted"> Đang tải kỳ trước…</span>}
                    {/*
                      * Nói thẳng kỳ trước là KỲ NÀO. "Cùng độ dài" không đủ để người đọc biết đang
                      * so với khoảng nào: lọc tháng 9 (30 ngày) thì kỳ trước là 02/08–31/08, không
                      * phải cả tháng 8 — một chi tiết không ai đoán ra và ai cũng đoán sai.
                      */}
                    {compareOpen && previousRangeLabel && !loading && (
                        <span className="VcMuted"> Kỳ trước: {previousRangeLabel}</span>
                    )}
                </div>
            )}

            {byStage && efficiencyThresholds && (
                <StageEfficiencyChart
                    stages={(report && report.stages) || []}
                    thresholds={efficiencyThresholds}
                    /*
                     * ⚠️ Gác bằng `compareOpen`. `toggleCompare` CỐ Ý không gọi lại API khi đóng
                     *    (đỡ một lượt đọc `itemhistories`), nên `report` vẫn giữ nguyên `previous`
                     *    — không gác thì bấm "Ẩn so sánh với kỳ trước" chỉ đổi nhãn nút, còn vệt
                     *    nhạt kỳ trước, chú giải, cột chênh và tooltip vẫn nằm nguyên trên biểu đồ.
                     */
                    previousMedians={compareOpen && efficiencyTotals && efficiencyTotals.previous
                        ? efficiencyTotals.previous.stageMedians
                        : null}
                    belowLabel={`dưới ${formatPercent(efficiencyThresholds.b, 0)}`}
                />
            )}

            {/*
              * Bốn thông báo mức dữ liệu (FR-008) nằm trong `ReportNotices`.
              *
              * ⚠️ Gom ra component riêng vì `eslint-plugin-react-hooks` bản repo đang dùng đếm
              *    nhánh trên TOÀN hàm: hàm này đã ~1.400 dòng, và mỗi `{cond && ()}` thêm vào JSX
              *    của nó đẩy bộ đếm lên tới chỗ plugin báo NHẦM "useMemo is called conditionally"
              *    cho những hook không liên quan ở phía trên.
              */}
            <ReportNotices
                hoursUnavailable={!!(report && report.hoursUnavailable)}
                lowCoverageStages={lowCoverageStages}
                coverageThreshold={thresholds.dataCoverage}
                milestoneNotes={milestoneNotes}
                rateWarnings={rateWarnings}
                onOpenStandardTime={() => history.push(ROUTES.STANDARD_TIME_STAGES)}
            />


            <Table
                className="VcTable VcOutputTable VcStickyHead"
                rowKey="key"
                columns={mergedColumns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                size="middle"
                /*
                 * Rộng thêm cho nhóm cột hiệu suất (5 cột ở màn theo người, 6 ở màn theo công
                 * đoạn). Thiếu chỗ thì antd bóp mọi cột lại cho vừa khung và con số không đọc nổi.
                 *
                 * ⚠️ Con số này là TỔNG `width` của các cột, nên sửa `width` của BẤT KỲ cột nào mà
                 *    quên sửa đây là bảng thừa/thiếu chỗ — thừa thì có một dải trống bên phải,
                 *    thiếu thì antd bóp cột và con số không đọc nổi.
                 *
                 *    Hiện: 1045 (gồm caret 40 + cột Tài khoản 150 + hai cột metadata 240) + 425
                 *    (hiệu suất theo người) hoặc + 540 (theo công đoạn). Trừ 240 khi hai cột
                 *    metadata đang ẩn. Khớp ĐÚNG tổng, không dư: bản trước khai 1510/1625 trong
                 *    khi tổng là 1500/1615, tức tự để lại đúng cái dải trống mà dòng trên cảnh báo.
                 *
                 * ⚠️ Mọi `width` trong file này ĐO ĐƯỢC, không ước lượng: dựng chuỗi dài nhất mà
                 *    từng cột thật sự render trên dữ liệu prod tháng 10, đo bề rộng chữ trong
                 *    chromium đúng font/cỡ của ô bảng, cộng 16px padding của `size="middle"`. Chặn
                 *    dưới của tiêu đề là TỪ dài nhất (chữ không ngắt giữa từ), nên tiêu đề được
                 *    phép xuống 2-3 dòng — cột hẹp theo DỮ LIỆU, không theo độ dài nhãn.
                 *    Số đo đáng nhớ: `↳ nguyenlq@foobla.com` = 152px, thẻ `Theo dữ liệu file in`
                 *    = 132px, `3.711,89 h` đậm = 67px.
                 *
                 * ⚠️ Cột Tài khoản là NGOẠI LỆ của luật trên: nó HẸP HƠN dữ liệu dài nhất một cách
                 *    có chủ đích và để tên xuống dòng — xem ghi chú tại chính cột đó.
                 *
                 * ⚠️ KHÔNG dùng prop `sticky` của antd cho tiêu đề ghim: nó chỉ có từ **antd 4.6.0**
                 *    còn repo đang **4.5.4**, nên prop đó bị BỎ QUA IM LẶNG — không lỗi, không
                 *    cảnh báo, chỉ là tiêu đề không ghim và không ai biết vì sao. Ghim bằng CSS
                 *    `position: sticky` trên `.ant-table-thead th` (lớp `VcStickyHead`), chạy trên
                 *    mọi bản antd.
                 */
                scroll={{x: (byStage ? 1585 : 1470) - (anyExpanded ? 0 : META_COLUMNS_WIDTH)}}
                locale={{
                    emptyText: (
                        <div className="VcEmptyState">
                            <div>Không có dữ liệu ghi nhận trong khoảng thời gian đã chọn.</div>
                            <div className="VcHint">
                                Thử mở rộng khoảng thời gian hoặc bỏ bớt điều kiện lọc.
                            </div>
                        </div>
                    ),
                }}
                onRow={(row) => ({
                    className: [
                        row.type === 'actor' ? 'VcRow--clickable VcRow--actor' : '',
                        row.type === 'stage' ? 'VcRow--child' : '',
                        row.type === 'warning' ? 'VcRow--warning' : '',
                    ].join(' '),
                    onClick: (event) => {
                        if (row.type !== 'actor') return
                        // Bấm vào con số không làm mở rộng dòng (B-AC-11).
                        if (event.target.closest('.VcClickableNumber')) return

                        setExpanded({...expanded, [row.key]: !expanded[row.key]})
                    },
                })}
            />

            {/*
              * Ma trận nhân viên × công đoạn — chỉ khi người dùng ĐÃ thu hẹp (`shouldShowMatrix`).
              *
              * ⚠️ Không phải chuyện hiệu năng mà là chuyện đọc được: lưới 200 × 9 của toàn xưởng
              *    không ai dò nổi, và nó đẩy hộp "Cách đọc số liệu" ra khỏi màn hình.
              *
              * ⚠️ Gác bằng `applied`, KHÔNG bằng `actors`/`stageCodes` của ô Select. Hai ô đó gọi
              *    `setFilter` ngay lúc chọn, còn `fetchReport` chỉ chạy khi bấm "Áp dụng" — nên
              *    bản trước bật ma trận ngay khi người dùng vừa chọn một người, và dựng nó trên
              *    `report` CŨ của toàn xưởng. Lưới hiện ra với 200 dòng, đúng lúc người dùng vừa
              *    nói họ chỉ muốn xem một người.
              */}
            {!byStage && efficiencyTotals && (
                <EfficiencyMatrix
                    actors={(report && report.actors) || []}
                    stages={matrixStages}
                    focusStageCode={matrixFocusStage}
                    narrowed={shouldShowMatrix(appliedFilters)}
                    onSelectActor={selectActor}
                />
            )}

            {/* UI-09: hộp Cách đọc số liệu luôn hiển thị, không thu gọn, không ẩn. */}
            <Notice level="warning" className="VcReadingBox">
                <div className="VcReadingTitle">Cách đọc số liệu</div>
                <ul className="VcReadingList">
                    {outputReadingNotesOf(thresholds).map((note) => <li key={note}>{note}</li>)}
                </ul>
            </Notice>

            {/*
              * Bốn modal dưới đây CÙNG một khuôn: `closable={false}` để tắt nút `×` của antd, tiêu đề
              * và nút "Đóng" nằm trong thân modal (`VcPageHead`).
              *
              * Vì sao bỏ `×`: mỗi modal vốn đã mang theo phần đầu trang của chính nó, nên có hai lối
              * đóng nằm cách nhau vài chục pixel và làm đúng một việc. Nhốt về một chỗ thì mọi modal
              * trong màn này đóng theo cùng một cách, không phải nhớ cái nào có gì.
              *
              * Phím Esc vẫn đóng được — `closable` chỉ tắt cái nút, không tắt bàn phím.
              */}
            <Modal
                visible={!!dropped}
                footer={null}
                closable={false}
                width={800}
                zIndex={1050}
                onCancel={() => setDropped(null)}
            >
                <div className="VcEmbeddedPage">
                    <div className="VcPageHead">
                        <div>
                            <h1 className="VcPageTitle">Dòng log bị loại</h1>
                        </div>
                        <div className="VcPageActions">
                            <Button onClick={() => setDropped(null)}>Đóng</Button>
                        </div>
                    </div>
                </div>
                <Table
                    className="VcTable"
                    rowKey={(row) => `${row.barcode}-${row.actionCode}-${row.logTime}`}
                    size="small"
                    pagination={false}
                    dataSource={dropped || []}
                    columns={[
                        {
                            title: 'Thời gian',
                            dataIndex: 'logTime',
                            /*
                             * ⚠️ Giờ VN, cùng đồng hồ với phụ đề trang. Dùng `formatDateTime` (giờ
                             *    MÁY) ở đây là một màn hình có HAI đồng hồ: đo mốc
                             *    `2026-10-28T02:00Z` ở `America/New_York` thì cột này in 27/10
                             *    22:00 còn phụ đề in 28/10 09:00 — lệch nguyên một ngày, ngay dưới
                             *    dòng ghi "(ngày giờ Việt Nam)". (Vòng review 7, #12.)
                             */
                            render: formatVnDateTime,
                            width: 150,
                        },
                        {title: 'Tài khoản', dataIndex: 'actor', width: 120},
                        {title: 'Mã thao tác', dataIndex: 'actionCode', render: (value) => <code>{value}</code>},
                        {title: 'Barcode', dataIndex: 'barcode', render: (value) => <code>{value}</code>},
                        {
                            title: 'Lý do loại',
                            dataIndex: 'reason',
                            render: (reason) => DROP_REASON_LABELS[reason] || reason,
                        },
                    ]}
                />
            </Modal>

            {/*
              * SCR-M18-002 nhúng làm Modal (width 95%) thay vì điều hướng route — không có `title`
              * riêng: OutputDetailPage tự có phần đầu trang (tiêu đề) trong nội dung, đặt thêm title
              * ở Modal sẽ lặp. Đóng bằng nút X có sẵn của Modal, không thêm nút "Đóng" trùng ý.
              * `destroyOnClose` để mỗi lần mở là một phiên fetch mới, không giữ trang cũ của lượt xem
              * trước. `bodyStyle` giảm padding-top: không có title bar nên 24px mặc định + margin
              * của tiêu đề bên trong cộng lại thành khoảng trắng quá cao phía trên "Chi tiết sản lượng".
              *
              * `zIndex=1050`: sidebar của app dùng `$sidebar-zindex` = 1040
              * (assets/scss/variables/_variables-custom.scss) — cao hơn z-index mặc định 1000 của
              * antd Modal, nên modal bị sidebar đè lên. Đặt cao hơn 1040 để modal luôn nổi trên.
              */}
            <Modal
                visible={!!detail}
                footer={null}
                closable={false}
                width="95%"
                destroyOnClose
                zIndex={1050}
                bodyStyle={{paddingTop: 12}}
                onCancel={closeDetail}
            >
                {detail && (
                    <OutputDetailPage
                        // `history`/`location` của CHÍNH trang báo cáo — OutputDetailPage vẫn gọi
                        // `useUrlFilters` vô điều kiện (giữ thứ tự hook), dù ở nhánh embedded nó
                        // không dùng giá trị trả về; thiếu hai prop này sẽ crash vì đọc thẳng
                        // `location.search`.
                        history={history}
                        location={location}
                        embedded
                        params={detail}
                        onClose={closeDetail}
                        onApplyFilters={applyDetailFilters}
                        stageOptions={stageOptions}
                        actorOptions={actorOptions}
                    />
                )}
            </Modal>

            {/*
              * Hai modal dưới CÙNG cơ chế với modal chi tiết: trạng thái nằm trên URL (`suspectOpen`,
              * `glossaryOpen`) chứ không phải `useState`, nên copy đường dẫn gửi cho người khác là họ
              * mở ra đúng thứ mình đang xem — đó là lý do không dùng state cục bộ cho chỗ này.
              */}
            <Modal
                visible={!!suspect}
                footer={null}
                closable={false}
                width="95%"
                destroyOnClose
                zIndex={1050}
                bodyStyle={{paddingTop: 12}}
                onCancel={closeSuspect}
            >
                {suspect && (
                    <SuspectPage
                        history={history}
                        location={location}
                        embedded
                        params={suspect}
                        onClose={closeSuspect}
                    />
                )}
            </Modal>

            <Modal
                visible={glossaryOpen}
                footer={null}
                closable={false}
                width={900}
                destroyOnClose
                zIndex={1050}
                bodyStyle={{paddingTop: 12}}
                onCancel={closeGlossary}
            >
                <GlossaryPage embedded onClose={closeGlossary}/>
            </Modal>
        </div>
    )
}

export default OutputReportPage
